import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { testSeriesService, assessmentService } from '../services/testSeriesService'
import type { ApiResponse, TestSeriesResponse, PagedAssessmentResponse, CompleteAssessmentResponse } from '../types'

/**
 * Hook to fetch all test series
 */
export function useAllTestSeries(): UseQueryResult<TestSeriesResponse[], Error> {
  return useQuery({
    queryKey: ['test-series-list'],
    queryFn: () => testSeriesService.getAllTestSeries(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (res) => res.data,
  })
}

/**
 * Hook to fetch test series by code
 * @param seriesCode - Test series code
 */
export function useTestSeries(
  seriesCode: string
): UseQueryResult<ApiResponse<TestSeriesResponse>, Error> {
  return useQuery({
    queryKey: ['test-series', seriesCode],
    queryFn: () => testSeriesService.getTestSeriesByCode(seriesCode),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!seriesCode,
  })
}

/**
 * Hook to fetch assessments in a test series
 * @param seriesCode - Test series code
 * @param page - Page number (0-indexed)
 * @param size - Number of items per page
 */
export function useTestSeriesAssessments(
  seriesCode: string,
  page = 0,
  size = 20
): UseQueryResult<PagedAssessmentResponse, Error> {
  return useQuery({
    queryKey: ['test-series-assessments', seriesCode, page, size],
    queryFn: () => testSeriesService.getAssessmentsInSeries(seriesCode, page, size),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!seriesCode,
  })
}

/**
 * Hook to fetch complete assessment with sections and questions
 * @param testCode - Assessment code (e.g., "NEET_2025_FLT_001")
 */
export function useCompleteAssessment(
  testCode: string
): UseQueryResult<ApiResponse<CompleteAssessmentResponse>, Error> {
  return useQuery({
    queryKey: ['assessment', 'complete', testCode],
    queryFn: () => assessmentService.getCompleteAssessment(testCode),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!testCode,
  })
}
